/* eslint-disable @typescript-eslint/ban-ts-comment */
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Avatar from '@deprecated-ui/common/Avatar';
import { IconWrapper } from '@deprecated-ui/common/Iconmoon';
import TagDefault from '@deprecated-ui/common/Tag';
import color from '@deprecated-ui/_style/color';
import { AVATAR_URL } from 'constants/api';
import Image from 'global/Image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { handleErrorAvatar } from 'utils/general';
import { LabelVerifiedAccount } from 'global/Labels';
import { useDispatch } from 'react-redux';
import {
  PAGE_SOURCE,
  mainProfileEvents,
} from 'features/user/analytics/ProfileAnalytics';
import useBondsAnalytics from 'global/TradingModal/RealTradingModal/ModalBonds/useBondsAnalytics';
import { CatalogItem, NavLink } from './styled';
import { effects } from './slice';
import { SearchSuggestionList, SearchType } from '../../../../@types/search';

const { Tag } = TagDefault;

export const CustomAvatar = ({ img, name }) => {
  const url = img.match(/http/g) ? img : `${AVATAR_URL}/${img}`;

  if (!img) {
    return <div />;
  }

  return (
    <Avatar size="x-small">
      <Image
        alt={name}
        src={url}
        onError={(e) => {
          handleErrorAvatar(e, name);
        }}
      />
    </Avatar>
  );
};

CustomAvatar.propTypes = {
  name: PropTypes.string.isRequired,
  img: PropTypes.string.isRequired,
};

const LabelComponent: React.FunctionComponent<{
  item: any;
  searchType: string;
}> = ({ item, searchType }) => {
  const dispatch = useDispatch();
  const { FRBondActionTrackers } = useBondsAnalytics();

  const postSearchRecent = (keyword: string, target: number, type: string) =>
    dispatch(
      effects.postSearchRecentResult({
        keyword,
        target,
        type,
      }),
    );

  if (searchType === 'sector') {
    const url = item.parent_alias1
      ? `${item.parent_alias1}/${item.name}`
      : item.name;

    const eventClick =
      item.alias1 === 'fr-bonds'
        ? () => FRBondActionTrackers.onFRProductListClicked('Search Page')
        : null;

    return (
      <Link href={`/catalog/${url}`} passHref>
        <CatalogItem onClick={eventClick}>{item.name}</CatalogItem>
      </Link>
    );
  }

  if (searchType === 'user') {
    return (
      <NavLink
        onClick={() => {
          postSearchRecent(item.name, item.id, 'people');
          mainProfileEvents.onViewProfile(PAGE_SOURCE.search);
        }}
      >
        <CustomAvatar img={item.img} name={item.name} />
        {/* @ts-expect-error */}
        <Flex direction="column" pl="10px" style={{ gap: '2px' }}>
          <Flex align="center" style={{ gap: '2px' }}>
            {/* @ts-expect-error */}
            <Text size="14px" mt="2px" family="bold">
              {item.keyword || item.name}
            </Text>
            <LabelVerifiedAccount deprecatedVerifiedStatus={item.is_verified} />
          </Flex>
          <Text size="12px" color={color.highgray}>
            {item.desc || item.name}
          </Text>
        </Flex>
      </NavLink>
    );
  }

  if (searchType === 'insider') {
    return (
      <NavLink onClick={() => postSearchRecent(item.label, item.id, 'insider')}>
        <IconWrapper>
          <Image src="https://ui.lab.stockbit.com/SBPD-18690/icon/icon-bandar-2.svg" />
        </IconWrapper>
        {/* @ts-expect-error */}
        <Text pl="10px" size="13px">
          {item.label}
        </Text>
      </NavLink>
    );
  }
  return (
    <NavLink onClick={() => postSearchRecent(item.name, item.id, 'company')}>
      <Tag size="xs" type="grey" style={{ fontFamily: 'ProximaNovaBd' }}>
        {item.keyword || item.name}
      </Tag>
      {/* @ts-expect-error */}
      <Text pl="10px" size="13px">
        {item.desc || item.name}
      </Text>
    </NavLink>
  );
};

const renderLabel = (item: SearchSuggestionList) => {
  const catalogKey = '_type';
  const type = item.type ? item.type : item[catalogKey];
  return <LabelComponent item={item} searchType={String(type).toLowerCase()} />;
};

export const recentOptions = (suggestions: {
  list: SearchSuggestionList[];
}) => {
  if (!suggestions.list) {
    return [];
  }

  if (suggestions.list.length === 0) {
    return [];
  }

  return [
    {
      label: 'Recent Searches',
      options: suggestions.list.map((item) => ({
        value: `suggestion-${item.id}-${item.keyword}`,
        label: renderLabel(item),
      })),
    },
  ];
};

const options = (suggestions: Record<SearchType, SearchSuggestionList[]>) => {
  if (!suggestions) {
    return [];
  }

  const company =
    suggestions.company.length > 0
      ? {
          label: 'Stocks',
          options: suggestions.company.map((item) => ({
            value: `stock-${item.id}-${item.keyword || item.name}`,
            label: renderLabel(item),
          })),
        }
      : undefined;

  const people =
    suggestions.people.length > 0
      ? {
          label: 'People',
          options: suggestions.people.map((item) => ({
            value: `people-${item.id}-${item.name}`,
            label: renderLabel(item),
          })),
        }
      : undefined;

  const insider =
    suggestions.insider.length > 0
      ? {
          label: 'Insider',
          options: suggestions.insider.map((item) => ({
            value: `insider-${item.id}`,
            label: renderLabel(item),
          })),
        }
      : undefined;

  const catalog =
    suggestions.sector.length > 0
      ? {
          label: 'Catalog',
          options: [
            {
              label: (
                <Flex wrap="wrap" style={{ gap: '10px' }}>
                  {suggestions.sector.map((item) => (
                    <LabelComponent item={item} searchType="sector" />
                  ))}
                </Flex>
              ),
              value: 'catalog-sector',
              className: 'catalog-res',
            },
          ],
        }
      : undefined;

  const list = [catalog, company, people, insider];

  return list.filter((item) => item !== undefined);
};

export default options;
